// ─── Gérer mes ingrédients ET mes catégories ───────────────────────
// Ouverte depuis ⚙️ sur l'écran Ingrédients. `base`/`baseRayons` servent à
// afficher le nom des éléments masqués (on n'a que leur id dans perso).
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, EmptyState, useUI, useT } from '@mop/core';

export default function EcranGererIngredients({
  visible, base, baseRayons, perso,
  onDemasquer, onRetirerAjout, onAnnulerModif,
  onDemasquerRayon, onRetirerRayonAjout, onAnnulerModifRayon,
  onFermer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  if (!visible) return null;

  const ajouts = perso?.ajouts || [];
  const masques = (perso?.masques || []).map(id => base.find(b => b.id === id)).filter(Boolean);
  const modifs = Object.entries(perso?.modifs || {})
    .map(([id, champs]) => ({ id, original: base.find(b => b.id === id), ...champs }))
    .filter(m => m.original);

  const rayonsAjouts = perso?.rayonsAjouts || [];
  const rayonsMasques = (perso?.rayonsMasques || []).map(id => baseRayons.find(r => r.id === id)).filter(Boolean);
  const rayonsModifs = Object.entries(perso?.rayonsModifs || {})
    .map(([id, champs]) => ({ id, original: baseRayons.find(r => r.id === id), ...champs }))
    .filter(m => m.original);

  return (
    <Feuille visible titre={t('ingredients.gerer')} emoji="⚙️" onFermer={onFermer}>
      <Text style={s.grandTitre}>{t('ingredients.titre')}</Text>

      <Text style={s.sect}>{t('ingredients.mesAjouts', { n: ajouts.length })}</Text>
      {ajouts.length === 0 ? <EmptyState icon="➕" texte={t('ingredients.aucunAjout')} /> : ajouts.map(i => (
        <View key={i.id} style={s.ligne}>
          <Text style={s.nom} numberOfLines={1}>{i.nom}{i.unite ? `  ·  ${i.unite}` : ''}</Text>
          <TouchableOpacity style={s.btn} onPress={() => onRetirerAjout(i.id)}>
            <Text style={s.btnTxt}>{t('commun.supprimer')}</Text>
          </TouchableOpacity>
        </View>
      ))}

      <Text style={s.sect}>{t('ingredients.mesCorrections', { n: modifs.length })}</Text>
      {modifs.length === 0 ? <EmptyState icon="✏️" texte={t('ingredients.aucuneCorrection')} /> : modifs.map(m => (
        <View key={m.id} style={s.ligne}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={s.nom} numberOfLines={1}>{m.nom}{m.unite ? `  ·  ${m.unite}` : ''}</Text>
            <Text style={s.original} numberOfLines={1}>{t('ingredients.original')} : {m.original.nom}</Text>
          </View>
          <TouchableOpacity style={[s.btn, s.btnAnnuler]} onPress={() => onAnnulerModif(m.id)}>
            <Text style={[s.btnTxt, s.btnAnnulerTxt]}>{t('ingredients.reafficher')}</Text>
          </TouchableOpacity>
        </View>
      ))}

      <Text style={s.sect}>{t('ingredients.mesMasques', { n: masques.length })}</Text>
      {masques.length === 0 ? <EmptyState icon="🙈" texte={t('ingredients.aucunMasque')} /> : masques.map(i => (
        <View key={i.id} style={s.ligne}>
          <Text style={s.nom} numberOfLines={1}>{i.nom}{i.unite ? `  ·  ${i.unite}` : ''}</Text>
          <TouchableOpacity style={[s.btn, s.btnAnnuler]} onPress={() => onDemasquer(i.id)}>
            <Text style={[s.btnTxt, s.btnAnnulerTxt]}>{t('ingredients.reafficher')}</Text>
          </TouchableOpacity>
        </View>
      ))}

      <Text style={[s.grandTitre, { marginTop: 22 }]}>{t('ingredients.categories')}</Text>

      <Text style={s.sect}>{t('ingredients.mesAjoutsCategorie', { n: rayonsAjouts.length })}</Text>
      {rayonsAjouts.length === 0 ? <EmptyState icon="➕" texte={t('ingredients.aucunAjoutCategorie')} /> : rayonsAjouts.map(r => (
        <View key={r.id} style={s.ligne}>
          <Text style={s.nom} numberOfLines={1}>{r.emoji} {r.label}</Text>
          <TouchableOpacity style={s.btn} onPress={() => onRetirerRayonAjout(r.id)}>
            <Text style={s.btnTxt}>{t('commun.supprimer')}</Text>
          </TouchableOpacity>
        </View>
      ))}

      <Text style={s.sect}>{t('ingredients.mesCorrectionsCategorie', { n: rayonsModifs.length })}</Text>
      {rayonsModifs.length === 0 ? <EmptyState icon="✏️" texte={t('ingredients.aucuneCorrectionCategorie')} /> : rayonsModifs.map(m => (
        <View key={m.id} style={s.ligne}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={s.nom} numberOfLines={1}>{m.emoji} {m.label}</Text>
            <Text style={s.original} numberOfLines={1}>{t('ingredients.original')} : {m.original.emoji} {m.original.label}</Text>
          </View>
          <TouchableOpacity style={[s.btn, s.btnAnnuler]} onPress={() => onAnnulerModifRayon(m.id)}>
            <Text style={[s.btnTxt, s.btnAnnulerTxt]}>{t('ingredients.reafficher')}</Text>
          </TouchableOpacity>
        </View>
      ))}

      <Text style={s.sect}>{t('ingredients.mesMasquesCategorie', { n: rayonsMasques.length })}</Text>
      {rayonsMasques.length === 0 ? <EmptyState icon="🙈" texte={t('ingredients.aucunMasqueCategorie')} /> : rayonsMasques.map(r => (
        <View key={r.id} style={s.ligne}>
          <Text style={s.nom} numberOfLines={1}>{r.emoji} {r.label}</Text>
          <TouchableOpacity style={[s.btn, s.btnAnnuler]} onPress={() => onDemasquerRayon(r.id)}>
            <Text style={[s.btnTxt, s.btnAnnulerTxt]}>{t('ingredients.reafficher')}</Text>
          </TouchableOpacity>
        </View>
      ))}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  grandTitre: { fontSize: 13, fontWeight: '800', color: UI.faint, textTransform: 'uppercase', letterSpacing: 0.6 },
  sect: { fontSize: 12, fontWeight: '800', color: UI.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 16, marginBottom: 8 },
  ligne: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, padding: 12, marginBottom: 6 },
  nom: { flex: 1, fontSize: 14, color: UI.ink, fontWeight: '600' },
  original: { fontSize: 11.5, color: UI.faint, marginTop: 2 },
  btn: { paddingVertical: 6, paddingHorizontal: 11, borderRadius: 999, backgroundColor: UI.danger || '#c0392b' },
  btnTxt: { fontSize: 12, fontWeight: '700', color: '#fff' },
  btnAnnuler: { backgroundColor: UI.accent },
  btnAnnulerTxt: { color: '#fff' },
});
